import { type CSSProperties } from 'typestyle/lib/types';
import { cssRaw, style } from 'typestyle';
import StyleConstants from '../../../core/styles.constants';

export const sectionCard: CSSProperties = {
  padding: '18px 20px',
  background: StyleConstants.WHITE_COLOR,
  border: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
  borderRadius: 10,
  marginTop: 20,
};

export const sectionTitle: CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  color: StyleConstants.DARK_BLUE_COLOR,
  marginBottom: 12,
};

export const headerRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 12,
  marginBottom: 16,
};

export const newButtonWrap: CSSProperties = {
  width: 180,
};

export const emptyStateWrap: CSSProperties = {
  padding: '32px 16px',
  textAlign: 'center',
};

export const emptyStateTitle: CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: StyleConstants.DARK_BLUE_COLOR,
  marginBottom: 8,
};

export const emptyStateDescription: CSSProperties = {
  fontSize: 13,
  color: StyleConstants.GREY_COLOR,
  marginBottom: 16,
  maxWidth: 420,
  marginLeft: 'auto',
  marginRight: 'auto',
};

export const emptyStateButtonWrap: CSSProperties = {
  width: 200,
  marginLeft: 'auto',
  marginRight: 'auto',
};

// A stable class (not just a style object) so the row-hover rule below can be scoped to it.
export const tableWrap = style({
  overflowX: 'auto',
  minWidth: 0,
  borderRadius: 10,
  border: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
});

cssRaw(
  `
  .${tableWrap} tbody tr:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
  `,
);

export const table: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

export const th: CSSProperties = {
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.3,
  whiteSpace: 'nowrap',
  color: StyleConstants.GREY_COLOR,
  background: 'rgba(0, 0, 0, 0.02)',
  borderBottom: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
  padding: '10px 12px',
};

export const td: CSSProperties = {
  fontSize: 13,
  color: StyleConstants.GREY_COLOR,
  borderBottom: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
  padding: '10px 12px',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
};

type DisplayStatus = 'pending' | 'live' | 'paused' | 'revoked';

const statusPalette: Record<DisplayStatus, { bg: string; color: string }> = {
  pending: { bg: 'rgba(255, 182, 62, 0.18)', color: '#8a5a00' },
  live: { bg: 'rgba(118, 186, 27, 0.18)', color: '#4b7a0c' },
  paused: { bg: 'rgba(192, 192, 192, 0.3)', color: StyleConstants.GREY_COLOR },
  revoked: { bg: 'rgba(229, 1, 91, 0.12)', color: StyleConstants.PINK_COLOR },
};

export const statusBadge = (status: DisplayStatus): CSSProperties => ({
  fontSize: 10.5,
  fontWeight: 700,
  padding: '3px 8px',
  borderRadius: 999,
  background: statusPalette[status].bg,
  color: statusPalette[status].color,
  whiteSpace: 'nowrap',
});

export const actionsCell: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};

export const smallButton: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  padding: '5px 10px',
  borderRadius: 6,
  border: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
  background: StyleConstants.WHITE_COLOR,
  color: StyleConstants.DARK_BLUE_COLOR,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export const smallButtonDanger: CSSProperties = {
  ...smallButton,
  color: StyleConstants.PINK_COLOR,
  borderColor: 'rgba(229, 1, 91, 0.35)',
};

export const pairingInput: CSSProperties = {
  padding: '5px 8px',
  border: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
  borderRadius: 6,
  fontSize: 12,
  color: StyleConstants.GREY_COLOR,
  width: 110,
  textTransform: 'uppercase',
};

export const errorText: CSSProperties = {
  fontSize: 11,
  color: StyleConstants.PINK_COLOR,
  marginTop: 4,
};

// Visually hidden (not display:none — that would break document.execCommand('copy') selection),
// used only as a real DOM node the copy-with-fallback logic can select() and copy from.
export const hiddenLinkInput: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: 'none',
};

// Hand-rolled switch rather than MUI's, to match the surrounding typestyle controls. The
// component renders it as a real <button role="switch" aria-checked>, so keyboard and
// screen-reader behaviour is unaffected by the custom visuals.
export const publicToggle = (isOn: boolean, isDisabled: boolean): CSSProperties => ({
  position: 'relative',
  width: 40,
  height: 22,
  flexShrink: 0,
  borderRadius: 999,
  border: 'none',
  padding: 0,
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  opacity: isDisabled ? 0.4 : 1,
  background: isOn ? '#4b7a0c' : StyleConstants.LIGHT_GREY_COLOR,
  transition: 'background-color .15s ease',
});

export const publicToggleKnob = (isOn: boolean): CSSProperties => ({
  position: 'absolute',
  top: 3,
  left: isOn ? 21 : 3,
  width: 16,
  height: 16,
  borderRadius: '50%',
  background: StyleConstants.WHITE_COLOR,
  transition: 'left .15s ease',
});

export const linkCopyButton: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: StyleConstants.DARK_BLUE_COLOR,
  background: StyleConstants.WHITE_COLOR,
  border: `1px solid ${StyleConstants.LIGHT_GREY_COLOR}`,
  borderRadius: 6,
  padding: '5px 10px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export const formRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
  alignItems: 'flex-end',
  marginBottom: 12,
};

export const formField: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  minWidth: 200,
};

export const fieldLabel: CSSProperties = {
  fontSize: 11.5,
  fontWeight: 600,
  color: StyleConstants.DARK_BLUE_COLOR,
};

export const buttonRow: CSSProperties = {
  display: 'flex',
  gap: 10,
  alignItems: 'center',
  flexWrap: 'wrap',
};

export const buttonWrap: CSSProperties = {
  width: 160,
};
